import { FunctionComponent, useMemo } from "react";

import DevMessaging from "src/components/DevMessaging";
import type { DevWarningsNode } from "src/config/devWarnings";
import {
  useA11yWarnings,
  useIsDev,
  useIsTouchLikeDevice,
  usePageWeightWarnings,
  usePerformanceWarnings,
} from "src/hooks";

export interface DevMessagesProps {
  /** Article/resource node (blocks etc.) used to evaluate warning checks. */
  resource: DevWarningsNode;
  /** Optional CSS selector for the root to run axe-core on (e.g. "#content"). Only in dev. */
  a11yRootSelector?: string | null;
  /** Function that returns sync warning messages for the given node. Use createGetDevWarnings(credits) from src/config/devWarnings. */
  getDevWarnings: (node: DevWarningsNode) => string[];
}

const DevMessagesInner: FunctionComponent<DevMessagesProps> = ({
  resource,
  a11yRootSelector = null,
  getDevWarnings,
}) => {
  const syncWarnings = useMemo(
    () => getDevWarnings(resource),
    [resource, getDevWarnings],
  );

  /** Custom a11y sync warnings (e.g. blank alt); only shown after axe has run. */
  const { syncA11yWarnings, otherSyncWarnings } = useMemo(() => {
    const a11y: string[] = [];
    const other: string[] = [];
    for (const msg of syncWarnings) {
      if (msg.startsWith("Accessibility:")) {
        a11y.push(msg);
      } else {
        other.push(msg);
      }
    }
    return { syncA11yWarnings: a11y, otherSyncWarnings: other };
  }, [syncWarnings]);

  const { warnings: a11yWarnings, hasRun: a11yHasRun } = useA11yWarnings(
    a11yRootSelector ?? null,
  );
  const {
    warnings: performanceWarnings,
    hasRun: performanceHasRun,
    metrics: performanceMetrics,
  } = usePerformanceWarnings();
  const {
    warnings: pageWeightWarnings,
    hasRun: pageWeightHasRun,
    metrics: pageWeightMetrics,
  } = usePageWeightWarnings();

  /** Include custom a11y sync warnings only after axe has run; keeps a11y messaging in sync. */
  const warnings = useMemo(
    () => [
      ...otherSyncWarnings,
      ...(a11yHasRun ? syncA11yWarnings : []),
      ...a11yWarnings,
      ...performanceWarnings,
      ...pageWeightWarnings,
    ],
    [
      otherSyncWarnings,
      syncA11yWarnings,
      a11yHasRun,
      a11yWarnings,
      performanceWarnings,
      pageWeightWarnings,
    ],
  );

  const passedCheckLabels = useMemo(() => {
    const labels: string[] = [];
    const a11yPassed =
      a11yHasRun && a11yWarnings.length === 0 && syncA11yWarnings.length === 0;
    if (a11yPassed) {
      labels.push(
        "A11y has run and found no accessibility errors on the page.",
      );
    }
    if (performanceHasRun && performanceWarnings.length === 0) {
      const parts: string[] = [];
      if (performanceMetrics?.lcpDeltaMs !== undefined) {
        parts.push(`FCP→LCP ${Math.round(performanceMetrics.lcpDeltaMs)}ms`);
      }
      if (performanceMetrics?.cls !== undefined && performanceMetrics.cls > 0) {
        parts.push(`CLS ${performanceMetrics.cls.toFixed(2)}`);
      }
      const perfMsg =
        parts.length > 0
          ? `Performance: ${parts.join(", ")} — within acceptable thresholds.`
          : "Performance: within acceptable thresholds.";
      labels.push(perfMsg);
    }
    if (
      pageWeightHasRun &&
      pageWeightWarnings.length === 0 &&
      pageWeightMetrics
    ) {
      const mb = (pageWeightMetrics.transferBytes / (1024 * 1024)).toFixed(1);
      let msg = `Page weight: ${mb}MB (excl. app bundle), ${pageWeightMetrics.requestCount} requests — within acceptable limits.`;
      if (pageWeightMetrics.excludedResource) {
        const excludedMb = (
          pageWeightMetrics.excludedResource.transferSize /
          (1024 * 1024)
        ).toFixed(1);
        msg += ` Excluded: ${pageWeightMetrics.excludedResource.name} — ${excludedMb}MB.`;
      }
      labels.push(msg);
    }
    return labels;
  }, [
    a11yHasRun,
    a11yWarnings.length,
    syncA11yWarnings.length,
    performanceHasRun,
    performanceWarnings.length,
    performanceMetrics?.lcpDeltaMs,
    performanceMetrics?.cls,
    pageWeightHasRun,
    pageWeightWarnings.length,
    pageWeightMetrics,
  ]);

  const inProgressMessages = useMemo(() => {
    const messages: string[] = [];
    if (a11yRootSelector !== null && a11yRootSelector !== "" && !a11yHasRun) {
      messages.push("A11y checking the page for accessibility errors");
    }
    if (!performanceHasRun) {
      messages.push("Performance: measuring FCP→LCP delta, CLS");
    }
    if (!pageWeightHasRun) {
      messages.push("Page weight: measuring transfer size and request count");
    }
    return messages;
  }, [a11yRootSelector, a11yHasRun, performanceHasRun, pageWeightHasRun]);

  return (
    <DevMessaging
      warnings={warnings}
      passedCheckLabels={passedCheckLabels}
      inProgressMessages={inProgressMessages}
    />
  );
};

/**
 * Evaluates dev warning definitions against the resource and runs a11y, performance,
 * and page weight checks in dev; renders DevMessaging when in development.
 * Centralised so all article-style stories use the same component and hooks.
 */
const DevMessages: FunctionComponent<DevMessagesProps> = (props) => {
  const isDev = useIsDev();
  const isTouchLike = useIsTouchLikeDevice();

  if (!isDev || isTouchLike) {
    return null;
  }

  return <DevMessagesInner {...props} />;
};

export default DevMessages;
